import React, { useState, useEffect, useRef, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import {
  Grid,
  Box,
  Typography,
  Button,
  Alert,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import DeviceInfoAndLocation from "../../global/DeviceInfoAndLocation";
import TechRelevanceForm from "../TechRelevanceForm";
import Loading from "./Loading";
import ErrorComponent from "./Error";
import {
  checkYear,
  checkSkillType,
  getTopSkill,
  DataEncryption,
  DataDecryption,
} from "../../utils/relevanceHelpers";
import { getEmail } from "../../utils/storage";
import {
  storeSkillsInLocalStorage,
  storeRelevanceResult,
} from "../../utils/skillsUtils";
import ReactGA4 from "react-ga4";
import { LinearProgress } from "@mui/material";

// Function to render custom icons
const renderCustomIcon = (iconName, size = 24) => {
  const iconPath = `/assets/images/bookmark-icons/${iconName}.svg`;
  return (
    <img
      src={iconPath}
      alt={iconName}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
      }}
      onError={(e) => {
        console.warn(`Failed to load custom icon: ${iconName}`);
        e.target.style.display = "none";
      }}
    />
  );
};

function BasicRelevanceCheck({ isLoggedIn, onLoginClick, onViewFullInsights }) {
  const isMobile = useMediaQuery("(max-width:600px)");
  const methods = useForm({ criteriaMode: "all" });
  
  // Feature flags
  const staticRandomEncodeDecodeString = useFeatureFlag(
    "staticRandomEncodeDecodeString"
  );
  const secretEncodeDecodeString = useFeatureFlag("secretEncodeDecodeString");

  // State
  const [relevanceResult, setRelevanceResult] = useState("");
  const [loadingButton, setLoadingButton] = useState(false);
  const [autoValue, setAutoValue] = useState([]);
  const [errorAPI, setErrorAPI] = useState(false);
  const [errorWithStatus, setErrorWithStatus] = useState(null);
  const [location, setLocation] = useState();
  const [deviceInfo, setDeviceInfo] = useState();
  const [skillLoading, setSkillLoading] = useState(false);
  const [topSkillValidity, setTopSkillValidity] = useState([]);
  const [synonyms, setSynonyms] = useState([]);
  const [addSkill, setAddSkill] = useState(false);
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
  const [skillLength, setSkillLength] = useState(0);
  const [shouldAutoClick, setShouldAutoClick] = useState(false);
  const [skillValidationComplete, setSkillValidationComplete] = useState(false);
  
  const autoClickExecutedRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const cancelToken = useRef(null);

  // Get device info and location
  useEffect(() => {
    DeviceInfoAndLocation(setLocation, setDeviceInfo);
  }, []);

  // Callback to handle skill validation completion
  const handleSkillValidationComplete = useCallback(() => {
    setSkillValidationComplete(true);
  }, []);

  // Function to get skill options (for TechRelevanceForm)
  function getSkillOptions(
    setData,
    endpoint,
    setLoading,
    setErrorWithStatus,
    setErrorAPI
  ) {
    setData([]);
    setLoading(true);

    if (cancelToken.current) {
      cancelToken.current.cancel("Previous request cancelled");
    }

    try {
      cancelToken.current = axios.CancelToken.source();

      axios
        .get(process.env.REACT_APP_API_BASE_URL + endpoint, {
          cancelToken: cancelToken.current.token,
        })
        .then((object) => {
          if (object?.status === 201) {
            const decryptedData = DataDecryption(
              object.data,
              secretEncodeDecodeString,
              staticRandomEncodeDecodeString
            );
            // Remove "Other" option
            const filteredData = Array.isArray(decryptedData)
              ? decryptedData.filter((n) => n.name !== "Other")
              : [];
            setData(filteredData);
          } else {
            setData([]);
          }
          setLoading(false);
          setErrorAPI(false);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            console.info("Request canceled:", error.message);
          } else {
            let errorMessage = "An unexpected error occurred";
            let errorStatus = null;

            if (error?.response) {
              errorStatus = error.response.status;
              if (errorStatus === 500) {
                errorMessage = "Server error occurred. Please try again later.";
              } else if (errorStatus === 404) {
                errorMessage = "Endpoint not found. Please check the URL.";
              } else if (errorStatus >= 400) {
                errorMessage = "Client-side error occurred.";
              }
            } else {
              errorMessage = "Network error, please check your connection.";
            }

            setErrorWithStatus({ message: errorMessage, status: errorStatus });
            setErrorAPI(true);
            setTimeout(() => {
              setLoading(false);
            }, 2000);
          }
        });
    } catch (error) {
      setErrorWithStatus({
        message: "An unexpected error occurred",
        status: null,
      });
      setTimeout(() => {
        setLoading(false);
      }, 2000);
      setErrorAPI(true);
    }
  }

  // Tech relevance handler
  const onSubmit = useCallback(
    async (data) => {
      // Prevent multiple simultaneous submissions
      if (isSubmittingRef.current && loadingButton) {
        return;
      }

      if (autoValue.length === 0) {
        methods.setError("primarySkill", {
          type: "required",
          message: `Please Enter a skill`,
        });
        setRelevanceResult("");
        return;
      }

      // Set submitting flag
      isSubmittingRef.current = true;
      setLoadingButton(true);
      setErrorAPI(false);
      setSkill({});
      setCheckSkill("");

      // Email is optional - only include if user is logged in
      const userEmail = getEmail();
      if (userEmail) {
        data.email = userEmail;
      }
      data.userLocation = location;
      data.deviceInfo = deviceInfo;
      data.primarySkill = data.primarySkill;

      setSkillLength(methods.getValues("primarySkill")?.length);

      try {
        axios
          .post(
            process.env.REACT_APP_API_BASE_URL + "/get-relevanceResult",
            DataEncryption(
              data,
              secretEncodeDecodeString,
              staticRandomEncodeDecodeString
            ),
            {
              options: {
                type: "json",
              },
            }
          )
          .then((object) => {
            const decryptedData = DataDecryption(
              object.data,
              secretEncodeDecodeString,
              staticRandomEncodeDecodeString
            );
            setAddSkill(false);
            setRelevanceResult(decryptedData);
            setLoadingButton(false);

            // Reset submitting flag on success
            isSubmittingRef.current = false;

            // Track key conversion event in GA4 only when result is successfully saved to MongoDB
            if (decryptedData?.relevanceResultId) {
              ReactGA4.event("check_relevance_homepage", {
                primary_skill: decryptedData?.topPrimarySkill?.[0]?.name || "",
                relevance_result_id: decryptedData.relevanceResultId,
              });
            }

            // Store skills for transfer to /skill-relevance page
            if (decryptedData?.topPrimarySkill) {
              const skillNames = decryptedData.topPrimarySkill.map(
                (skill) => skill.name
              );
              storeSkillsInLocalStorage(skillNames);
            }

            // Store full relevance result data to avoid duplicate API call
            storeRelevanceResult(decryptedData);

            // Scroll to results
            setTimeout(() => {
              const element = document?.getElementById("home-relevance-results");
              element?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
          })
          .catch((error) => {
            let errorMessage = "An unexpected error occurred";
            let errorStatus = null;

            if (error?.response) {
              errorStatus = error.response.status;
              if (
                errorStatus === 500 &&
                error?.response?.data?.message === "Email verification failed"
              ) {
                errorMessage = "Email not valid !!";
                methods.setError("email", {
                  type: "validation",
                  message: errorMessage,
                });
                setRelevanceResult("");
              } else if (errorStatus === 404) {
                errorMessage = "API endpoint not found";
              } else if (errorStatus >= 400) {
                errorMessage = "Client-side error occurred";
              } else if (errorStatus >= 500) {
                errorMessage = "Internal server error occurred";
              }
            } else {
              errorMessage = "Network error, please check your connection";
            }
            setErrorWithStatus({ message: errorMessage, status: errorStatus });
            setErrorAPI(true);
            setLoadingButton(false);
            isSubmittingRef.current = false;
          });
      } catch (error) {
        setErrorWithStatus({
          message: "An unexpected error occurred",
          status: null,
        });
        setLoadingButton(false);
        setErrorAPI(true);
        isSubmittingRef.current = false;
      }
    },
    [
      autoValue,
      loadingButton,
      methods,
      location,
      deviceInfo,
      secretEncodeDecodeString,
      staticRandomEncodeDecodeString,
    ]
  );

  // Dummy state setters for TechRelevanceForm compatibility
  const [skill, setSkill] = useState({});
  const [checkSkill, setCheckSkill] = useState("");

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        color: "#fff",
        py: { xs: 6, sm: 8, md: 10, lg: 12 },
        px: { xs: 2, sm: 3, md: 4, lg: 6 },
        overflow: "hidden",
      }}
      id="check-relevance-section"
    >
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={11} md={10} lg={9} xl={8}>
          {/* Title Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 1.5, sm: 2, md: 2.5 },
              mb: { xs: 4, sm: 5, md: 6 },
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: { xs: "none", sm: "block" },
                maxWidth: { xs: "40px", sm: "45px", md: "50px" },
              }}
            >
              {renderCustomIcon("enter-skills", 50)}
            </Box>
            <Typography
              sx={{
                fontFamily:
                  "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                fontWeight: 600,
                fontSize: {
                  xs: "24px",
                  sm: "32px",
                  md: "40px",
                  lg: "48px",
                },
                lineHeight: {
                  xs: "28px",
                  sm: "38px",
                  md: "48px",
                  lg: "58px",
                },
                textAlign: "center",
                color: "#fff",
                letterSpacing: { xs: "-0.02em", md: "-0.01em" },
              }}
            >
              Check Your Skills Relevance
            </Typography>
          </Box>

          {/* Form Section */}
          <FormProvider {...methods}>
            <form
              id="home-tech-relevance"
              onSubmit={methods.handleSubmit(onSubmit)}
            >
              <TechRelevanceForm
                setSkillLoading={setSkillLoading}
                setRelevanceResult={setRelevanceResult}
                methods={methods}
                DataDecryption={(jsonString) =>
                  DataDecryption(
                    jsonString,
                    secretEncodeDecodeString,
                    staticRandomEncodeDecodeString
                  )
                }
                DataEncryption={(json) =>
                  DataEncryption(
                    json,
                    secretEncodeDecodeString,
                    staticRandomEncodeDecodeString
                  )
                }
                setTopSkillValidity={setTopSkillValidity}
                setSynonyms={setSynonyms}
                setAutoValue={setAutoValue}
                shouldAutoFocus={shouldAutoFocus}
                setShouldAutoFocus={setShouldAutoFocus}
                getEmail={getEmail}
                skillLength={skillLength}
                autoValue={autoValue}
                topSkillValidity={topSkillValidity}
                deviceInfo={deviceInfo}
                addSkill={addSkill}
                onSubmit={onSubmit}
                setErrorWithStatus={setErrorWithStatus}
                setErrorAPI={setErrorAPI}
                location={location}
                getSkillOptions={getSkillOptions}
                skillLoading={skillLoading}
                shouldAutoClick={shouldAutoClick}
                autoClickExecutedRef={autoClickExecutedRef}
                onSkillValidationComplete={handleSkillValidationComplete}
              />
              <Box py={4}>
                {loadingButton ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      my: 5,
                    }}
                  >
                    <Loading />
                  </Box>
                ) : (
                  <Button
                    id={"check-relevance-button-homepage"}
                    type="submit"
                    form="home-tech-relevance"
                    sx={{
                      maxWidth: { xs: "100%", sm: "350px", md: "400px" },
                      width: "100%",
                      display: "block",
                      m: "0px auto",
                      color: "#FFFFFF",
                      py: { xs: "12px", sm: "14px", md: "16px" },
                      px: { xs: "24px", sm: "32px", md: "40px" },
                      fontSize: {
                        xs: "16px",
                        sm: "18px",
                        md: "20px",
                        lg: "24px",
                      },
                      fontWeight: 700,
                      borderRadius: "30px",
                      textTransform: "none",
                      fontStyle: "bold",
                      lineHeight: {
                        xs: "24px",
                        sm: "27px",
                        md: "30px",
                      },
                      backgroundColor: "transparent !important",
                      backgroundImage:
                        "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      transition: "all 0.3s linear",
                      fontFamily:
                        "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                      boxShadow: "0px 4px 12px rgba(102, 82, 228, 0.3)",
                      "&:hover": {
                        border: "1px solid rgba(255, 255, 255, 0.4)",
                        background: "#FFFFFF !important",
                        color: "rgba(108,82,229,1)",
                        transition: "all 0.3s linear",
                        boxShadow: "0px 6px 16px rgba(102, 82, 228, 0.5)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                        cursor: "not-allowed",
                        color: "rgba(255, 255, 255, 0.5)",
                      },
                    }}
                    disabled={skillLoading}
                  >
                    Check Relevance
                  </Button>
                )}
              </Box>
            </form>
          </FormProvider>

          {/* Error Display */}
          {errorAPI && (
            <Box sx={{ mt: 2 }}>
              <ErrorComponent errorWithStatus={errorWithStatus} />
            </Box>
          )}

          {/* Results Section */}
          {relevanceResult?.length !== 0 &&
          relevanceResult !== undefined &&
          !loadingButton &&
          relevanceResult?.topPrimarySkill?.length > 0 ? (
            <Box id="home-relevance-results" sx={{ mt: 6 }}>
              {/* Results Title */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 1.5, sm: 2 },
                  mb: { xs: 4, sm: 5, md: 6 },
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                    maxWidth: { xs: "35px", sm: "40px" },
                  }}
                >
                  {renderCustomIcon("relevance-result", 40)}
                </Box>
                <Typography
                  sx={{
                    fontFamily:
                      "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    fontSize: {
                      xs: "24px",
                      sm: "32px",
                      md: "40px",
                      lg: "48px",
                    },
                    lineHeight: {
                      xs: "28px",
                      sm: "38px",
                      md: "48px",
                      lg: "58px",
                    },
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#fff",
                    letterSpacing: { xs: "-0.02em", md: "-0.01em" },
                  }}
                >
                  How long you will remain relevant ?
                </Typography>
              </Box>

              <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} rowSpacing={{ xs: 2, sm: 3 }}>
                {/* Top Skill Validity Card */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: { xs: "20px", sm: "25px", md: "30px", lg: "40px" },
                      border: "1px solid rgba(102,82,228,0.3)",
                      borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                      boxShadow: "0px 0px 16px 0px rgba(102,82,228,0.2)",
                      height: "100%",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      backdropFilter: "blur(10px)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0px 0px 24px 0px rgba(102,82,228,0.3)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", lg: "row" },
                        alignItems: { xs: "center", lg: "flex-start" },
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: { xs: "150px", lg: "188px" },
                          width: "100%",
                          mb: { xs: 2, lg: 0 },
                          mr: { lg: 2 },
                        }}
                      >
                        <img
                          src="/assets/images/timer2.gif"
                          style={{
                            width: "100%",
                            height: "auto",
                            maxWidth: "192px",
                          }}
                          alt="timer"
                          loading="lazy"
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontFamily:
                              "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            fontSize: { xs: "20px", sm: "24px", md: "28px", lg: "32px" },
                            fontWeight: 600,
                            textAlign: { xs: "center", lg: "left" },
                            color: "#fff",
                            mb: { xs: 1, sm: 1.5 },
                          }}
                        >
                          Top Skill Validity Until
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily:
                              "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            fontSize: { xs: "40px", sm: "48px", md: "55px", lg: "65px" },
                            fontWeight: 700,
                            textAlign: { xs: "center", lg: "left" },
                            color: "#6652E4",
                            lineHeight: "1.1",
                          }}
                        >
                          {
                            getTopSkill(relevanceResult?.topPrimarySkill)
                              .maxExpiryYear
                          }
                          <Typography
                            variant="subtitle2"
                            sx={{ fontSize: "14px", display: "block", mt: 0.5 }}
                          >
                            (approximately)
                          </Typography>
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          {
                            getTopSkill(relevanceResult?.topPrimarySkill)
                              ?.labelSuggestion
                          }
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* Skill Score Card */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: { xs: "20px", sm: "25px", md: "30px", lg: "35px" },
                      border: "2px solid rgba(102,82,228,0.3)",
                      borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                      boxShadow: "0px 0px 16px 0px rgba(102,82,228,0.2)",
                      height: "100%",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      backdropFilter: "blur(10px)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0px 0px 24px 0px rgba(102,82,228,0.3)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 3,
                        justifyContent: { xs: "center", md: "flex-start" },
                      }}
                    >
                      <Box sx={{ maxWidth: { xs: "46px", md: "58px" } }}>
                        <img
                          src="/assets/images/skill-score-relevance-01.svg"
                          width="100%"
                          height="auto"
                          alt="skill-score"
                          loading="lazy"
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily:
                            "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                          fontWeight: 600,
                          fontSize: { xs: "20px", sm: "24px", md: "28px", lg: "32px" },
                          color: "#fff",
                        }}
                      >
                        Skill score
                      </Typography>
                    </Box>

                    {autoValue?.length >= 5 && (
                      <Alert
                        severity="success"
                        sx={{
                          mb: 2,
                          backgroundColor: "rgba(46, 125, 50, 0.2)",
                          border: "1px solid rgba(46, 125, 50, 0.3)",
                          color: "#fff",
                          "& .MuiAlert-icon": {
                            color: "#4caf50",
                          },
                        }}
                      >
                        Displaying the top 5 skills based on the most recent
                        validity year.
                      </Alert>
                    )}

                    {relevanceResult?.topPrimarySkill?.map((item, index) => {
                      return (
                        <Box
                          key={item._id || index}
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: { xs: 2, sm: 3 },
                            alignItems: { xs: "flex-start", sm: "center" },
                            justifyContent: "space-between",
                            my: { xs: "20px", md: "25px" },
                          }}
                        >
                          <Box sx={{ flex: 1, width: "100%" }}>
                            <Typography
                              sx={{
                                fontFamily:
                                  "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                fontSize: { xs: "18px", sm: "20px" },
                                fontWeight: 600,
                                mb: { xs: "6px", sm: "8px" },
                                color: "#fff",
                              }}
                            >
                              {item?.name}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: { xs: 1.5, sm: 2 },
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                                flexWrap: { xs: "wrap", sm: "nowrap" },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: { xs: "14px", sm: "16px" },
                                  color: "rgba(255, 255, 255, 0.8)",
                                  fontFamily:
                                    "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                }}
                              >
                                {item.type === "y"
                                  ? checkYear(item?.expiryYear)?.label
                                  : checkSkillType(item?.expiryYear)}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: { xs: "20px", sm: "22px" },
                                  fontWeight: 700,
                                  color:
                                    checkYear(item?.expiryYear)
                                      ?.percentageColor || "#6652E4",
                                  fontFamily:
                                    "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                }}
                              >
                                {item?.score <= 0
                                  ? "20"
                                  : item?.score > 100
                                  ? "80"
                                  : Math.round(item?.score)}
                                %
                              </Typography>
                            </Box>
                            <Box sx={{ width: "100%" }}>
                              <LinearProgress
                                sx={{
                                  height: "10px",
                                  borderRadius: "20px",
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor:
                                      checkYear(item?.expiryYear)
                                        ?.barPrimaryColor || "#6652E4",
                                  },
                                  backgroundColor:
                                    checkYear(item?.expiryYear)
                                      ?.barSecondaryColor || "#e0d5ff",
                                }}
                                variant="determinate"
                                value={
                                  item?.score <= 0
                                    ? 20
                                    : item?.score > 100
                                    ? 80
                                    : Math.round(item?.score)
                                }
                              />
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>

                {/* Info Alert */}
                <Grid item xs={12}>
                  <Alert
                    severity="info"
                    sx={{
                      backgroundColor: "rgba(102,82,228,0.1)",
                      border: "1px solid rgba(102,82,228,0.3)",
                      color: "#fff",
                      "& .MuiAlert-icon": {
                        color: "#6652E4",
                      },
                    }}
                  >
                    Recommendations and potential salary increase percentages
                    provided are for informational purposes only and should be
                    used as suggestions, not guarantees
                  </Alert>
                </Grid>

                {/* CTA Section */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: "1px solid rgba(102,82,228,0.3)",
                      borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                      background: "rgba(102,82,228,0.1)",
                      backdropFilter: "blur(10px)",
                      p: { xs: 3, sm: 4, md: 5 },
                      textAlign: "center",
                      mt: { xs: 2, sm: 3 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily:
                          "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        fontSize: { xs: "20px", sm: "24px", md: "28px", lg: "32px" },
                        fontWeight: 600,
                        color: "#fff",
                        mb: { xs: 1.5, sm: 2 },
                      }}
                    >
                      {isLoggedIn
                        ? "View Full Insights"
                        : "Unlock Full Insights"}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily:
                          "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        fontSize: { xs: "13px", sm: "14px", md: "16px" },
                        color: "rgba(255, 255, 255, 0.8)",
                        mb: { xs: 3, sm: 4 },
                        px: { xs: 1, sm: 0 },
                      }}
                    >
                      {isLoggedIn
                        ? "Get detailed insights including interview questions, game-changer insights, salary data, and more!"
                        : "Login to access detailed insights including interview questions, game-changer insights, salary data, and more!"}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => {
                        ReactGA4.event("view_full_insights_click");
                        if (isLoggedIn) {
                          onViewFullInsights();
                        } else {
                          onLoginClick();
                        }
                      }}
                      sx={{
                        backgroundColor: "transparent !important",
                        backgroundImage:
                          "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                        color: "#fff",
                        fontFamily:
                          "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        fontSize: { xs: "14px", sm: "16px", md: "18px" },
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: "30px",
                        px: { xs: 3, sm: 4, md: 5 },
                        py: { xs: "10px", sm: "12px", md: "14px" },
                        boxShadow: "0px 4px 12px rgba(102,82,228,0.3)",
                        transition: "all 0.3s linear",
                        "&:hover": {
                          border: "1px solid rgba(74,61,158,1)",
                          background: "#FFF !important",
                          color: "rgba(108,82,228,1)",
                          boxShadow: "0px 6px 16px rgba(102,82,228,0.4)",
                          transition: "all 0.3s linear",
                        },
                      }}
                    >
                      {isLoggedIn
                        ? "View Full Insights"
                        : "Login to View Full Insights"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
}

export default BasicRelevanceCheck;

